'use client';

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase/client';
import { authService, UserProfile } from '@/services/auth-service';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Ref to avoid stale closure in onAuthStateChange callback
  const profileRef = useRef<UserProfile | null>(null);
  profileRef.current = profile;

  useEffect(() => {
    // 1. Get initial user (getUser validates token server-side, unlike getSession)
    const initAuth = async () => {
      try {
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();

        if (error || !currentUser) {
          setUser(null);
          setProfile(null);
        } else {
          setUser(currentUser);
          const userProfile = await authService.getProfile(currentUser.id);
          setProfile(userProfile);
        }
      } catch (err) {
        console.error("Auth init error:", err);
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    };

    initAuth();

    // 2. Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      if (session?.user) {
        setUser(session.user);

        // Only fetch profile if we don't have it or it belongs to a different user
        if (!profileRef.current || profileRef.current.id !== session.user.id) {
          const userProfile = await authService.getProfile(session.user.id);
          setProfile(userProfile);
        }
      }
      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const isAdmin = profile?.role === 'admin';

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
